import React, { useState, useEffect } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Step1SectionMeta from "./Step1SectionMeta";
import Step2QuestionsForm from "./Step2QuestionsForm";

export default function AddSectionForm({
  paperId,
  defaultQuestionCount,
  onClose,
  onComplete,
}) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const [sectionData, setSectionData] = useState({
    sourceId: paperId,
    sourceType: "Paper",
    sectionName: "",
    difficulty: "medium",
    totalQuestions: defaultQuestionCount || "",
    paragraph: {
      isParagraph: false,
      text: "",
    },
    questions: [],
  });


  // Re-sync if prop updates (optional safety)
  useEffect(() => {
    if (defaultQuestionCount && !sectionData.totalQuestions) {
      setSectionData((prev) => ({
        ...prev,
        totalQuestions: defaultQuestionCount,
      }));
    }
  }, [defaultQuestionCount]);

  const handleAddQuestion = () => {
    setSectionData((prev) => {
      if (prev.questions.length >= prev.totalQuestions) {
        showErrorToast(`You can add only ${prev.totalQuestions} questions`);
        return prev;
      }
      return {
        ...prev,
        questions: [
          ...prev.questions,
          {
            questionNo: prev.questions.length + 1,
            questionType: sectionData.paragraph.isParagraph ? "paragraph" : "text",
            questionText: "",
            optionsType: "text",
            options: { A: "", B: "", C: "", D: "" },
            correctOption: "",
          },
        ],
      };
    });
  };

  const handleRemoveQuestion = (index) => {
    setSectionData((prev) => {
      const questions = prev.questions
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, questionNo: i + 1 }));
      return { ...prev, questions, totalQuestions: questions.length };
    });
  };

  const handleProceedToQuestions = () => {
    if (!sectionData.sectionName?.trim())
      return showErrorToast("Section name is required");
    if (!sectionData.totalQuestions || sectionData.totalQuestions < 1)
      return showErrorToast("Total questions must be at least 1");
    if (
      sectionData.paragraph.isParagraph &&
      !sectionData.paragraph.text?.trim()
    ) {
      showErrorToast("Section paragraph is required");
      return;
    }

    if (sectionData.questions.length === 0) {
      setSectionData((prev) => ({
        ...prev,
        questions: [
          {
            questionNo: 1,
            questionType: "text",
            questionText: "",
            options: { A: "", B: "", C: "", D: "" },
            optionsType: "text",
            correctOption: "",
          },
        ],
      }));
    }
    setStep(2);
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...sectionData.questions];
    if (field.includes("options.")) {
      const optKey = field.split(".")[1];
      updatedQuestions[index].options[optKey] = value;
    } else {
      updatedQuestions[index][field] = value;
    }
    setSectionData((prev) => ({ ...prev, questions: updatedQuestions }));
  };

  const handleCreateNewSection = async () => {
    for (let i = 0; i < sectionData.questions.length; i++) {
      const q = sectionData.questions[i];
      if (!q.questionText?.trim())
        return showErrorToast(`Question ${i + 1} text is missing`);
      if (!q.options.A || !q.options.B || !q.options.C || !q.options.D)
        return showErrorToast(`All options required for Q${i + 1}`);
      if (!q.correctOption)
        return showErrorToast(`Correct option missing for Q${i + 1}`);
    }

    try {
      setLoading(true);
      const payload = {
        sourceId: paperId,
        sourceType: "Paper",

        sectionName: sectionData.sectionName.trim(),
        difficulty: sectionData.difficulty,

        paragraph: {
          isParagraph: sectionData.paragraph.isParagraph,
          text: sectionData.paragraph.isParagraph
            ? sectionData.paragraph.text
            : "",
        },

        totalQuestions: Number(sectionData.totalQuestions),

        questions: sectionData.questions.map((q, i) => ({
          questionNo: i + 1,

          questionType: q.questionType,

          questionText:
            q.questionType === "paragraph"
              ? q.questionText
              : q.questionText.trim(),

          optionsType: q.optionsType,

          options: {
            A: q.options.A.trim(),
            B: q.options.B.trim(),
            C: q.options.C.trim(),
            D: q.options.D.trim(),
          },

          correctOption: q.correctOption,
        })),
      };


      await makeRequestAuth("post", "/admin/addQuestion", payload);
      showSuccessToast("Section added successfully!");
      onComplete?.();
    } catch (err) {
      showErrorToast(err.message || "Failed to add section");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-4xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in duration-200">
        <div className="bg-white px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-b gap-4 shrink-0 z-10">
          <div className="flex items-center gap-3 md:gap-6">
            <StepBadge
              num={1}
              active={step >= 1}
              completed={step > 1}
              label="Info"
            />
            <div
              className={`hidden sm:block h-[2px] w-10 md:w-16 ${step === 2 ? "bg-blue-500" : "bg-gray-200"}`}
            />
            <StepBadge
              num={2}
              active={step === 2}
              completed={false}
              label="Questions"
            />
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition"
          >
            <FiX size={24} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden relative">
          {step === 1 ? (
            <div className="h-full overflow-y-auto p-4 md:p-8">
              <Step1SectionMeta
                sectionData={sectionData}
                setSectionData={setSectionData}
                onNext={handleProceedToQuestions}
              />
            </div>
          ) : (
            <Step2QuestionsForm
              questions={sectionData.questions}
              totalLimit={sectionData.totalQuestions}
              onQuestionChange={handleQuestionChange}
              onAddQuestion={handleAddQuestion}
              onRemoveQuestion={handleRemoveQuestion}
              onBack={() => setStep(1)}
              onSave={handleCreateNewSection}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StepBadge({ num, active, completed, label }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${active ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-400"}`}
      >
        {completed ? <FiCheck size={16} /> : num}
      </div>
      <span
        className={`text-sm font-bold ${active ? "text-gray-800" : "text-gray-400"}`}
      >
        {label}
      </span>
    </div>
  );
}
